import React, { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { Database, Users, Building, CheckCircle, AlertCircle, Eye, Download } from 'lucide-react';

const ImportSampleDataPage = () => {
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<{ success: boolean; message: string; details?: any } | null>(null);

  const fetchApiData = async () => {
    if (!supabase) return;

    setImporting(true);
    setResult(null);

    try {
      // Call the SuccessWare import Edge Function to get real data structure
      const { data, error } = await supabase.functions.invoke('successware-import', {
        body: {
          action: 'fetch_customers',
          companies: ['hales-ac', 'climate-control', 'abraham-ac'],
          limit_per_company: 5,
          preview_mode: true // This will show us the data structure without saving
        }
      });

      if (error) throw error;

      setResult({
        success: true,
        message: 'Successfully fetched real customer data from SuccessWare API',
        details: data
      });

    } catch (error: any) {
      setResult({
        success: false,
        message: `API call failed: ${error.message || error}`,
        details: error
      });
    } finally {
      setImporting(false);
    }
  };

  const importAndSaveCustomers = async () => {
    if (!supabase) return;

    setImporting(true);
    setResult(null);

    try {
      // Call the SuccessWare import Edge Function to import and save real data
      const { data, error } = await supabase.functions.invoke('successware-import', {
        body: {
          action: 'import_customers',
          companies: ['hales-ac', 'climate-control', 'abraham-ac'],
          limit_per_company: 5,
          save_to_database: true
        }
      });

      if (error) throw error;

      setResult({
        success: true,
        message: 'Successfully imported and saved real customer data',
        details: data
      });

    } catch (error: any) {
      setResult({
        success: false,
        message: `Import failed: ${error.message || error}`,
        details: error
      });
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <div className="max-w-4xl mx-auto p-6">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">SuccessWare API Import</h1>
          <p className="text-slate-400">Import real customer data from SuccessWare API to analyze data structure</p>
        </div>

        <div className="bg-slate-800 rounded-lg p-6 mb-6">
          <div className="flex items-center mb-4">
            <Database className="h-6 w-6 text-blue-500 mr-3" />
            <h2 className="text-xl font-semibold">Real SuccessWare Data Import</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-slate-700 rounded-lg p-4">
              <div className="flex items-center mb-2">
                <Building className="h-5 w-5 text-green-500 mr-2" />
                <h3 className="font-medium">Hales AC</h3>
              </div>
              <p className="text-slate-400 text-sm">Pull 5 real customers from SuccessWare API</p>
            </div>
            <div className="bg-slate-700 rounded-lg p-4">
              <div className="flex items-center mb-2">
                <Building className="h-5 w-5 text-blue-500 mr-2" />
                <h3 className="font-medium">Climate Control Services</h3>
              </div>
              <p className="text-slate-400 text-sm">Pull 5 real customers from SuccessWare API</p>
            </div>
            <div className="bg-slate-700 rounded-lg p-4">
              <div className="flex items-center mb-2">
                <Building className="h-5 w-5 text-purple-500 mr-2" />
                <h3 className="font-medium">Abraham AC</h3>
              </div>
              <p className="text-slate-400 text-sm">Pull 5 real customers from SuccessWare API</p>
            </div>
          </div>

          <div className="bg-slate-900 rounded-lg p-4 mb-6">
            <h3 className="font-semibold mb-2">What This Will Do:</h3>
            <ul className="text-sm text-slate-300 space-y-1">
              <li>✅ Authenticate with SuccessWare API using real credentials</li>
              <li>✅ Fetch 5 customers from each company (15 total)</li>
              <li>✅ Show complete data structure from API</li>
              <li>✅ Analyze what fields we need in our database</li>
              <li>✅ Import and save to sw_customers table</li>
              <li>✅ Test our search and filtering functionality</li>
            </ul>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button
              onClick={fetchApiData}
              disabled={importing}
              className="bg-blue-600 hover:bg-blue-700 disabled:bg-slate-600 text-white px-6 py-3 rounded-lg transition-colors flex items-center justify-center"
            >
              {importing ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Fetching Data...
                </>
              ) : (
                <>
                  <Eye className="h-4 w-4 mr-2" />
                  Preview API Data
                </>
              )}
            </button>

            <button
              onClick={importAndSaveCustomers}
              disabled={importing}
              className="bg-green-600 hover:bg-green-700 disabled:bg-slate-600 text-white px-6 py-3 rounded-lg transition-colors flex items-center justify-center"
            >
              {importing ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Importing...
                </>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  Import & Save Data
                </>
              )}
            </button>
          </div>
        </div>

        {result && (
          <div className={`rounded-lg p-6 ${result.success ? 'bg-green-900/50 border border-green-700' : 'bg-red-900/50 border border-red-700'}`}>
            <div className="flex items-center mb-3">
              {result.success ? (
                <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
              ) : (
                <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
              )}
              <h3 className="font-semibold">API Result</h3>
            </div>
            <p className="mb-3">{result.message}</p>

            {result.details && (
              <div className="bg-slate-800 rounded p-3 max-h-96 overflow-auto">
                <pre className="text-sm text-slate-300 whitespace-pre-wrap">
                  {JSON.stringify(result.details, null, 2)}
                </pre>
              </div>
            )}

            {result.success && result.details && (
              <div className="mt-4">
                <p className="text-green-400 text-sm">
                  ✅ Real SuccessWare data fetched! Analyze the structure above to see what fields we need to add to our database.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ImportSampleDataPage;